// extractionPostProcess.js
// Shared post-processing for /api/extract-message responses.
// Validates and normalizes the LLM's structured extraction output.
//
// Imported by:
//   - backend-server.js  (production /api/extract-message handler)
//   - __tests__/extractionPersonas.test.mjs  (50-persona regression suite)
//
// Two safety nets are embedded here:
//   1. Landmark/neighborhood merging — when the LLM splits "Rome — Colosseum,
//      Vatican, Trastevere" into 4 destinations, recognize that 3 are
//      sub-locations of Rome and merge them into activities.
//   2. City-list rescue — when the LLM compresses "Auckland Wellington
//      Christchurch and Queenstown" into [{name: "New Zealand"}, {name:
//      "Queenstown"}], scan the original message for known cities and
//      promote any the LLM dropped back to their own destination, removing
//      the country-named placeholder.

import { getCityCountrySync } from './geographyConstants.js';

// Dynamic known-cities set for landmark merging.
// If a destination name matches this set, it's a real city. If it doesn't match
// but appears alongside one that does, it's likely a landmark/neighborhood.
// This approach is dynamic — add cities as needed, no per-city landmark lists required.
export const KNOWN_TRAVEL_CITIES = new Set([
  // Europe
  'rome', 'paris', 'london', 'barcelona', 'madrid', 'lisbon', 'porto', 'amsterdam',
  'berlin', 'munich', 'vienna', 'prague', 'budapest', 'athens', 'istanbul',
  'florence', 'venice', 'milan', 'naples', 'dubrovnik', 'split', 'copenhagen',
  'stockholm', 'oslo', 'helsinki', 'reykjavik', 'edinburgh', 'dublin', 'brussels',
  'zurich', 'geneva', 'interlaken', 'lucerne', 'salzburg', 'krakow', 'warsaw',
  'bucharest', 'sofia', 'belgrade', 'tallinn', 'riga', 'vilnius', 'nice', 'lyon',
  'marseille', 'seville', 'granada', 'valencia', 'malaga', 'santorini', 'mykonos',
  'crete', 'rhodes', 'corfu', 'palermo', 'amalfi', 'positano', 'cinque terre',
  'bruges', 'ghent', 'montenegro', 'kotor', 'mostar', 'tbilisi', 'batumi',
  'bergen', 'gothenburg', 'malta', 'valletta', 'luxembourg', 'monaco',
  // UK specific
  'manchester', 'liverpool', 'bath', 'oxford', 'cambridge', 'york', 'glasgow',
  'belfast', 'cardiff', 'brighton', 'bristol', 'inverness',
  // Asia
  'tokyo', 'kyoto', 'osaka', 'bangkok', 'singapore', 'hong kong', 'seoul',
  'taipei', 'beijing', 'shanghai', 'hanoi', 'ho chi minh city', 'saigon',
  'kuala lumpur', 'bali', 'ubud', 'jakarta', 'manila', 'cebu', 'phnom penh',
  'siem reap', 'luang prabang', 'vientiane', 'yangon', 'kathmandu',
  'delhi', 'new delhi', 'mumbai', 'jaipur', 'agra', 'goa', 'varanasi',
  'udaipur', 'kerala', 'kolkata', 'chennai', 'bangalore', 'hyderabad',
  'colombo', 'kandy', 'maldives', 'male', 'dubai', 'abu dhabi', 'doha',
  'muscat', 'amman', 'petra', 'tel aviv', 'jerusalem', 'beirut',
  'nara', 'hiroshima', 'fukuoka', 'sapporo', 'okinawa', 'nagoya', 'kobe',
  'chiang mai', 'chiang rai', 'phuket', 'krabi', 'koh samui', 'pattaya',
  'da nang', 'hoi an', 'halong bay', 'nha trang',
  // Americas
  'new york', 'new york city', 'nyc', 'los angeles', 'san francisco',
  'chicago', 'miami', 'boston', 'washington dc', 'seattle', 'portland',
  'denver', 'austin', 'nashville', 'new orleans', 'san diego', 'las vegas',
  'honolulu', 'maui', 'hawaii', 'anchorage', 'savannah', 'charleston',
  'philadelphia', 'atlanta', 'dallas', 'houston', 'phoenix', 'santa fe',
  'aspen', 'jackson hole', 'key west', 'napa valley', 'orlando',
  'mexico city', 'cancun', 'tulum', 'playa del carmen', 'oaxaca',
  'san miguel de allende', 'puerto vallarta', 'cabo san lucas',
  'havana', 'cartagena', 'medellin', 'bogota', 'lima', 'cusco', 'quito',
  'buenos aires', 'rio de janeiro', 'sao paulo', 'santiago', 'montevideo',
  'la paz', 'sucre', 'galapagos',
  'toronto', 'montreal', 'vancouver', 'quebec city', 'calgary', 'banff', 'whistler',
  // Africa
  'cape town', 'johannesburg', 'durban', 'nairobi', 'zanzibar', 'dar es salaam',
  'marrakech', 'fez', 'casablanca', 'chefchaouen', 'cairo', 'luxor', 'aswan',
  'accra', 'dakar', 'addis ababa', 'kigali', 'kampala', 'lagos', 'tunis',
  'windhoek', 'victoria falls', 'livingstone', 'madagascar', 'mauritius',
  'seychelles', 'reunion',
  // Oceania
  'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'cairns',
  'gold coast', 'hobart', 'auckland', 'queenstown', 'wellington', 'christchurch',
  'rotorua', 'fiji', 'bora bora', 'tahiti', 'moorea',
  // Middle East
  'riyadh', 'jeddah', 'medina', 'muscat', 'salalah', 'bahrain',
  // Caribbean
  'jamaica', 'barbados', 'aruba', 'curacao', 'st lucia', 'antigua',
  'san juan', 'nassau', 'bermuda', 'cayman islands', 'turks and caicos',
  'st barts', 'virgin islands',
  // Misc popular
  'patagonia', 'antarctica', 'svalbard', 'faroe islands', 'azores',
  'madeira', 'canary islands', 'tenerife', 'ibiza', 'mallorca', 'menorca',
  'sardinia', 'sicily', 'corsica', 'capri',
]);

// Country names — used to prevent merging countries as landmarks
export const KNOWN_COUNTRY_NAMES = new Set([
  'afghanistan', 'albania', 'algeria', 'argentina', 'armenia', 'australia',
  'austria', 'azerbaijan', 'bahamas', 'bahrain', 'bangladesh', 'barbados',
  'belarus', 'belgium', 'belize', 'bhutan', 'bolivia', 'bosnia', 'botswana',
  'brazil', 'brunei', 'bulgaria', 'cambodia', 'cameroon', 'canada', 'chile',
  'china', 'colombia', 'costa rica', 'croatia', 'cuba', 'cyprus', 'czechia',
  'czech republic', 'denmark', 'dominican republic', 'ecuador', 'egypt',
  'el salvador', 'england', 'estonia', 'ethiopia', 'fiji', 'finland', 'france',
  'georgia', 'germany', 'ghana', 'greece', 'guatemala', 'haiti', 'honduras',
  'hungary', 'iceland', 'india', 'indonesia', 'iran', 'iraq', 'ireland',
  'israel', 'italy', 'jamaica', 'japan', 'jordan', 'kazakhstan', 'kenya',
  'kuwait', 'kyrgyzstan', 'laos', 'latvia', 'lebanon', 'libya', 'lithuania',
  'luxembourg', 'madagascar', 'malaysia', 'maldives', 'mali', 'malta', 'mexico',
  'moldova', 'mongolia', 'montenegro', 'morocco', 'mozambique', 'myanmar',
  'namibia', 'nepal', 'netherlands', 'new zealand', 'nicaragua', 'nigeria',
  'north korea', 'norway', 'oman', 'pakistan', 'panama', 'paraguay', 'peru',
  'philippines', 'poland', 'portugal', 'qatar', 'romania', 'russia', 'rwanda',
  'saudi arabia', 'scotland', 'senegal', 'serbia', 'singapore', 'slovakia',
  'slovenia', 'south africa', 'south korea', 'spain', 'sri lanka', 'sudan',
  'sweden', 'switzerland', 'syria', 'taiwan', 'tajikistan', 'tanzania',
  'thailand', 'togo', 'trinidad and tobago', 'tunisia', 'turkey', 'turkmenistan',
  'uganda', 'ukraine', 'united arab emirates', 'uae', 'united kingdom', 'uk',
  'united states', 'usa', 'us', 'uruguay', 'uzbekistan', 'venezuela', 'vietnam',
  'wales', 'yemen', 'zambia', 'zimbabwe',
  // Aliases / SARs the rescue logic also needs to recognize so users typing
  // "Hong Kong and Macau" / "USA and Mexico" / "Britain and France" don't
  // miss the country half. (Audit found ext and cityDisambiguation drifted.)
  'korea', 'hong kong', 'macau', 'mauritius', 'seychelles',
  'america', 'united states of america', 'britain', 'great britain',
  'holland', 'czech republic',
]);

/**
 * Validates and normalizes the LLM extraction response.
 *
 * @param {object} raw - The parsed JSON response from the extraction LLM.
 * @param {string} originalMessage - The user's original message text.
 * @param {object} [options]
 * @param {boolean} [options.silent=false] - When true, suppress console.log diagnostics (useful in tests).
 * @returns {object} The validated extraction result with safe-net rescues applied.
 */
export function parseAndValidateExtraction(raw, originalMessage, options = {}) {
  const { silent = false } = options;
  const log = silent ? () => {} : (...args) => console.log(...args);
  const validActions = ['plan_trip', 'add_stop', 'remove_stop', 'modify', 'question', 'social'];

  let destinations = Array.isArray(raw.destinations)
    ? raw.destinations.map(d => ({
        name: typeof d === 'string' ? d : (d.name || ''),
        specific: typeof d === 'string' ? true : (d.specific !== false),
        days: typeof d === 'object' && typeof d.days === 'number' && d.days > 0 ? d.days : null,
        transportTo: typeof d === 'object' && typeof d.transportTo === 'string' ? d.transportTo : null,
      })).filter(d => d.name)
    : [];

  let activities = Array.isArray(raw.activities) ? raw.activities.filter(a => typeof a === 'string') : [];

  // POST-PROCESSING: Dynamic landmark/neighborhood merging
  // If the LLM splits "Rome - Colosseum, Vatican, Trastevere" into 4 destinations,
  // detect which are real cities vs landmarks/neighborhoods and merge accordingly.
  // Uses a known-cities set (major travel destinations) — anything NOT recognized as a city
  // that appears alongside a recognized city is likely a landmark/neighborhood within it.
  if (destinations.length > 1) {
    const isKnownCity = (name) => KNOWN_TRAVEL_CITIES.has(name.trim().toLowerCase());

    const recognizedCities = destinations.filter(d => isKnownCity(d.name));
    const unrecognizedNames = destinations.filter(d => !isKnownCity(d.name));

    // Helper: structural check — looks like a landmark/neighborhood by shape alone.
    // Excludes "City, Country" format, very long names, and country names.
    const isStructurallySubLocation = (d) => {
      const n = d.name.trim().toLowerCase();
      if (n.includes(',')) return false;
      if (n.split(/\s+/).length > 4) return false;
      if (KNOWN_COUNTRY_NAMES.has(n)) return false;
      return true;
    };

    // Cross-reference helper for Case 2: when there are MULTIPLE recognized
    // cities, an unrecognized name the user explicitly mentioned is likely a
    // standalone town the LLM doesn't know about (Merzouga, Imlil, Essaouira) —
    // keep it as a destination. In Case 1 (single city + sub-locations) this
    // check is skipped because the most common pattern is "Rome - Colosseum,
    // Vatican" where the user DID mention each landmark and they should still
    // be merged.
    const userExplicitlyMentioned = (d) => {
      if (!originalMessage) return false;
      const msgLower = originalMessage.toLowerCase();
      return msgLower.includes(d.name.trim().toLowerCase());
    };

    // Case 1: Exactly 1 recognized city + 1+ unrecognized names.
    // The unrecognized ones are landmarks/neighborhoods within that city —
    // merge regardless of whether the user mentioned them.
    if (recognizedCities.length === 1 && unrecognizedNames.length >= 1) {
      const toMerge = unrecognizedNames.filter(isStructurallySubLocation);
      if (toMerge.length > 0) {
        destinations = [...recognizedCities, ...unrecognizedNames.filter(d => !isStructurallySubLocation(d))];
        activities = [...activities, ...toMerge.map(l => l.name)];
        log(`🏛️ Merged ${toMerge.length} sub-locations into activities for ${recognizedCities[0].name}: ${toMerge.map(l => l.name).join(', ')}`);
      }
    }
    // Case 2: Multiple recognized cities + unrecognized names.
    // Apply cross-ref: only merge unrecognized names the user did NOT mention
    // (those are LLM hallucinations); keep user-mentioned ones as standalone
    // destinations (real-but-obscure towns).
    else if (recognizedCities.length >= 2 && unrecognizedNames.length >= 1) {
      const toMerge = unrecognizedNames.filter(d =>
        isStructurallySubLocation(d) && !userExplicitlyMentioned(d),
      );
      if (toMerge.length > 0) {
        destinations = [
          ...recognizedCities,
          ...unrecognizedNames.filter(d => !toMerge.includes(d)),
        ];
        activities = [...activities, ...toMerge.map(l => l.name)];
        log(`🏛️ Multi-city: merged ${toMerge.length} sub-locations into activities alongside ${recognizedCities.length} cities: ${toMerge.map(l => l.name).join(', ')}`);
      }
    }
  }

  // POST-PROCESSING SAFETY NET: rescue dropped cities when LLM compresses a
  // city-list into a country.
  // User: "Auckland Wellington Christchurch and Queenstown"
  // Bad LLM extraction: [{name: "New Zealand"}, {name: "Queenstown"}]
  // → Wellington and Christchurch silently lost.
  //
  // STRICTNESS GATES (added after a 50-persona audit found false-positive risk):
  //   (a) Country alias normalization — "UK" matches "United Kingdom",
  //       "USA" matches "United States", "Hong Kong" rescues under China.
  //   (b) Substring dedup — "york" inside "new york" doesn't double-rescue.
  //   (c) List-context gate — message must show explicit list separators
  //       (commas, "and", arrows, pure whitespace) between candidate cities.
  //       Prevents "I want york style pizza in Tokyo" from rescuing York.
  //   (d) Country-mismatch gate — only rescue cities whose country matches
  //       one of the country-named destinations the LLM produced. Bath in a
  //       Greece-only message doesn't trigger.
  //   (e) Word-boundary regex on both sides — "yorkshire" won't match York.
  if (originalMessage && destinations.length > 0) {
    const msgLower = originalMessage.toLowerCase();
    const isCountry = (name) => KNOWN_COUNTRY_NAMES.has(name.trim().toLowerCase());
    const countryDestinations = destinations.filter(d => isCountry(d.name));

    // Normalize country names so UK/United Kingdom/USA/US all unify.
    // Hong Kong & Macau are SARs of China — when getCityCountrySync says
    // "Hong Kong", we want it to match a "China" target country.
    const COUNTRY_ALIAS_NORM = {
      'uk': 'united kingdom',
      'great britain': 'united kingdom',
      'britain': 'united kingdom',
      'england': 'united kingdom',
      'scotland': 'united kingdom',
      'wales': 'united kingdom',
      'usa': 'united states',
      'us': 'united states',
      'united states of america': 'united states',
      'america': 'united states',
      'uae': 'united arab emirates',
      'hong kong': 'china',
      'macau': 'china',
      'holland': 'netherlands',
      'czechia': 'czech republic',
    };
    const normalizeCountry = (c) => {
      const lower = (c || '').trim().toLowerCase();
      return COUNTRY_ALIAS_NORM[lower] || lower;
    };

    if (countryDestinations.length > 0) {
      const targetCountriesNorm = new Set(
        countryDestinations.map(d => normalizeCountry(d.name)),
      );

      // Step 1: build candidate matches with positions, prefer longer names
      // so "new york" wins over "york" when they overlap.
      const existingNames = new Set(destinations.map(d => d.name.trim().toLowerCase()));
      const allMatches = [];
      const sortedCities = [...KNOWN_TRAVEL_CITIES].sort((a, b) => b.length - a.length);
      const claimedSpans = []; // [start, end) ranges already taken by a longer city
      for (const knownCity of sortedCities) {
        const escaped = knownCity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`\\b${escaped}\\b`, 'gi');
        let m;
        while ((m = re.exec(msgLower)) !== null) {
          const start = m.index;
          const end = start + knownCity.length;
          // Skip if a longer name already covers this span
          const overlaps = claimedSpans.some(([s, e]) => start < e && end > s);
          if (overlaps) continue;
          claimedSpans.push([start, end]);
          allMatches.push({ name: knownCity, start, end });
        }
      }
      allMatches.sort((a, b) => a.start - b.start);

      // Step 2: list-context gate.
      // Several signals can prove the user wrote a city-list (vs prose
      // mentioning cities incidentally). Any one is sufficient:
      //   • 3+ candidate cities → likely a list, even if separated by prose
      //     ("fly into Tokyo, spend a few days, then to Kyoto, and Osaka")
      //   • 2+ candidates with a pure-separator span between them
      //     ("Auckland Wellington" / "Bath and London")
      //   • Any candidate adjacent to a list connector ("and", ",", "→")
      //     ("Hong Kong and Macau" — only Hong Kong matches our set, but
      //      its trailing " and " marks list intent.)
      //   • Country mentioned in message + ≥1 candidate.
      const SEPARATOR_PAIR_RE = /^[\s,&;/\-]*(?:\s+(?:and|then|to|or|y|et|nach)\s+|\s*[→\-]+>?\s*|[\s,&;/])*[\s,&;/\-]*$/i;
      const ADJACENCY_BEFORE = /(?:^|[\s])(?:and|then|or|y|et|nach)\s*$|[,&;/]\s*$|->\s*$|→\s*$/i;
      const ADJACENCY_AFTER = /^\s*(?:and|then|or|y|et|nach)\s|^\s*[,&;/]|^\s*->|^\s*→/i;
      let hasListPair = false;
      for (let i = 0; i < allMatches.length - 1; i++) {
        const cur = allMatches[i];
        const nxt = allMatches[i + 1];
        const between = msgLower.slice(cur.end, nxt.start);
        if (between.length > 25) continue;
        if (SEPARATOR_PAIR_RE.test(between)) { hasListPair = true; break; }
      }
      const hasAdjacency = allMatches.some(m => {
        const before = msgLower.slice(Math.max(0, m.start - 10), m.start);
        const after = msgLower.slice(m.end, Math.min(msgLower.length, m.end + 10));
        return ADJACENCY_BEFORE.test(before) || ADJACENCY_AFTER.test(after);
      });
      const countryMentioned = countryDestinations
        .some(d => msgLower.includes(d.name.trim().toLowerCase()));
      const messageHasListContext =
        allMatches.length >= 3 ||
        hasListPair ||
        (allMatches.length >= 1 && hasAdjacency) ||
        (allMatches.length >= 1 && countryMentioned);

      if (messageHasListContext) {
        const rescued = [];
        for (const m of allMatches) {
          if (existingNames.has(m.name)) continue;
          // Gate (d): only skip when we KNOW the city's country and it
          // doesn't match the target. If the city's country is unknown
          // (e.g. "Bath" → null), trust the list-context gate that already
          // gave us strong evidence the user meant a list.
          const cityCountry = getCityCountrySync(m.name);
          if (cityCountry &&
              !targetCountriesNorm.has(normalizeCountry(cityCountry))) {
            continue;
          }
          const formatted = m.name.split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
          rescued.push({ name: formatted, specific: true, days: null, transportTo: null });
        }

        if (rescued.length > 0) {
          // Drop only the country-named destinations whose country matches a
          // rescued city. (Other countries stay — e.g., user said
          // "Italy and Tokyo Kyoto" → keep Italy, rescue Tokyo+Kyoto.)
          const rescuedCountriesNorm = new Set(
            rescued.map(r => normalizeCountry(getCityCountrySync(r.name.toLowerCase()) || '')),
          );
          destinations = [
            ...destinations.filter(d => {
              if (!isCountry(d.name)) return true;
              return !rescuedCountriesNorm.has(normalizeCountry(d.name));
            }),
            ...rescued,
          ];
          log(`🌐 City-list rescue: dropped country "${countryDestinations.map(d => d.name).join(', ')}" and added ${rescued.length} cities the user mentioned: ${rescued.map(r => r.name).join(', ')}`);
        }
      }
    }
  }

  return {
    action: validActions.includes(raw.action) ? raw.action : 'social',
    destinations,
    removals: Array.isArray(raw.removals) ? raw.removals.filter(r => typeof r === 'string') : [],
    duration: typeof raw.duration === 'number' && raw.duration > 0 && raw.duration <= 60 ? raw.duration : null,
    travelers: typeof raw.travelers === 'string' ? raw.travelers : null,
    occasion: typeof raw.occasion === 'string' ? raw.occasion : null,
    month: typeof raw.month === 'string' ? raw.month : null,
    activities,
    preferences: Array.isArray(raw.preferences) ? raw.preferences.filter(p => typeof p === 'string') : [],
    question: typeof raw.question === 'string' ? raw.question : null,
    modification: raw.modification && typeof raw.modification === 'object' ? raw.modification : null,
    sentiment: ['positive', 'negative', 'neutral'].includes(raw.sentiment) ? raw.sentiment : 'neutral',
    hotelPreference: typeof raw.hotelPreference === 'string' ? raw.hotelPreference : null,
    addAll: raw.addAll === true,
    requestedPlaces: Array.isArray(raw.requestedPlaces) ? raw.requestedPlaces.filter(p => typeof p === 'string') : [],
    transportMode: ['fly', 'drive', 'road-trip'].includes(raw.transportMode) ? raw.transportMode : null,
    originCity: typeof raw.originCity === 'string' && raw.originCity.trim() ? raw.originCity.trim() : null,
    returnCity: typeof raw.returnCity === 'string' && raw.returnCity.trim() ? raw.returnCity.trim() : null,
    arrivalTime: typeof raw.arrivalTime === 'string' && /^\d{1,2}:\d{2}$/.test(raw.arrivalTime) ? raw.arrivalTime : null,
  };
}
